import React, { useState, ChangeEvent, SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Form, Button, Card } from 'react-bootstrap';
import Loading from '../../common/Loading';
import CREATE_NAVSLOT_MUTATION from './queries/CREATE_NAVSLOT_MUTATION';
import SLOTS_QUERY from './queries/NAVSLOTS_QUERY';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import apolloClient from '../../utils/apolloClient';
import AlertDismissible from '../../common/AlertDismissible';

interface Item {
    id?: string;
    label?: string;
    description?: string;
    uri?: string;
}

interface Menu {
    id?: string;
    name?: string;
    description?: string;
    items?: Item[];
}

interface Slot {
    id?: string;
    name?: string;
    description?: string
    menu?: Menu
}
const CreateNavSlot = () => {
    const [validated, setValidated] = useState(false);
    const newItem = (key: number) => ({ id: `item-${key}`, label: '' });
    const navigate = useNavigate();
    const [slot, setSlot]: [Slot, Function] = useState({ menu: { name: '' } });
    const [menu, setMenu]: [Menu, Function] = useState({ name: '', description: '', items: [] });
    const [items, setItems]: [Item[], Function] = useState([newItem(0)]);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
        const form: HTMLFormElement = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        if (form.checkValidity()) {
            createNavSlot();
            setSaving(true);
        }
        setValidated(true);
    };
    const getEditedData = () => {
        menu.items = items;
        return {
            ...slot,
            menu
        };
    }
    const [createNavSlot, { loading }] = useMutation(CREATE_NAVSLOT_MUTATION, {
        variables: {
            navigationData: {
                ...getEditedData()
            },
        },
        onCompleted: (res) => {
            const toAdd = res.addNavSlot;
            const data = apolloClient.readQuery({ query: SLOTS_QUERY });
            apolloClient.writeQuery({
                query: SLOTS_QUERY,
                data: {
                    navigations: [...data.navigations, toAdd],
                },
            });
            setSaving(false);
            setSuccess(true);
            navigate(`/admin/edit-nav/${res.addNavSlot.slug}`)
        }
    });

    const renderItems = () => {
        return <div><h3>Items</h3>
            {items.map((item, i) => <Card key={`item-${i}`} className="px-4 py-2 mb-4">
                <Form.Group className="mt-2">
                    <Form.Label>Label</Form.Label>
                    <Form.Control required onChange={(event: ChangeEvent<any>) => {
                        const changedValue: string = event.target.value || '';
                        if (!item?.id) {
                            item.id = `item-${i}`;
                        }
                        const changedItems = items.map(ci => {
                            if (ci.id === item.id) {
                                return {
                                    ...ci,
                                    label: changedValue,
                                }
                            } else {
                                return ci;
                            }
                        });
                        setItems([
                            ...changedItems,
                        ]);
                    }} placeholder="Name" defaultValue={item.label} type="text" />
                </Form.Group>
                <Form.Group className="mt-2">
                    <Form.Label>Description</Form.Label>
                    <Form.Control onChange={(event: ChangeEvent<any>) => {
                        const changedValue: string = event.target.value || '';
                        if (!item?.id) {
                            item.id = `item-${i}`;
                        }
                        const changedItems = items.map(ci => {
                            if (ci.id === item.id) {
                                return {
                                    ...ci,
                                    description: changedValue,
                                }
                            } else {
                                return ci;
                            }
                        });
                        setItems([
                            ...changedItems,
                        ]);
                    }} placeholder="Description" defaultValue={item.description} type="text" />
                </Form.Group>
                <Form.Group className="mt-2">
                    <Form.Label>URI</Form.Label>
                    <Form.Control required onChange={(event: ChangeEvent<any>) => {
                        const changedValue: string = event.target.value || '';
                        if (!item?.id) {
                            item.id = `item-${i}`;
                        }
                        const changedItems = items.map(ci => {
                            if (ci.id === item.id) {
                                return {
                                    ...ci,
                                    uri: changedValue,
                                }
                            } else {
                                return ci;
                            }
                        });
                        setItems([
                            ...changedItems,
                        ]);
                    }} placeholder="URI" defaultValue={item.uri} type="text" />
                </Form.Group>
            </Card>)}
            <Button className="mb-3" variant="outline-primary" onClick={() => setItems([...items, newItem(items.length)])}>Add Another Item <FontAwesomeIcon icon="plus" /></Button>
        </div>;
    }

    const renderMenu = () => {
        return <div><h2>Menu</h2>
            <Card bg="light" className="px-4 py-2">
                <Form.Group id="MenuNameControl" className="mt-2">
                    <Form.Label>Menu Name</Form.Label>
                    <Form.Control required onChange={(event: ChangeEvent<any>) => {
                        const changed: string = event.target.value || '';
                        setMenu({
                            ...menu,
                            name: changed,
                        });
                    }} placeholder="Name" defaultValue={menu.name} type="text" />
                </Form.Group>
                <Form.Group className="mt-2" id="MenuDescControl">
                    <Form.Label>Menu Description</Form.Label>
                    <Form.Control onChange={(event: ChangeEvent<any>) => {
                        const changed: string = event.target.value || '';
                        setMenu({
                            ...menu,
                            description: changed,
                        });
                    }} placeholder="Description" defaultValue={menu.description} type="text" />
                </Form.Group>
                {renderItems()}
            </Card>
        </div>
    }
    const renderNavSlot = () => {
        return (<Form noValidate validated={validated} onSubmit={handleSubmit}>
            {loading && <div className="mt-4 mb-4"><Loading /></div>}
            {success && <AlertDismissible variant="success" dismissible onClose={() => setSuccess(false)} className="mt-4">NavSlot Saved.</AlertDismissible>}
            {<Form.Group className="mt-4" id="slotNameControl">
                <Form.Label>Name</Form.Label>
                <Form.Control required onChange={(event: ChangeEvent<any>) => {
                    const changed: string = event.target.value || '';
                    setSlot({
                        ...slot,
                        name: changed,
                    });
                }} placeholder="Name" defaultValue={slot.name} type="text" />
            </Form.Group>}
            {<Form.Group className="mt-4" id="slotDescControl">
                <Form.Label>Description</Form.Label>
                <Form.Control onChange={(event: ChangeEvent<any>) => {
                    const changed: string = event.target.value || '';
                    setSlot({
                        ...slot,
                        description: changed,
                    });
                }} placeholder="Description" defaultValue={slot.description} type="text" />
            </Form.Group>}
            {renderMenu()}
            <Button disabled={saving || loading} variant="primary" className="mt-4" type="submit">Create Nav</Button>
        </Form>);
    };
    return <div>{renderNavSlot()}</div>;
};

export default CreateNavSlot;
